// Parses a Grafana time-range query fragment into epoch-ms numbers.
//
// Accepts the kinds of strings users actually paste:
//   - "from=1781042361555&to=1781049375202"
//   - "?from=1781042361555&to=1781049375202"
//   - a full Grafana URL containing those params anywhere in the query
//
// Returns null on any failure (missing params, non-numeric, swapped, out of
// sane range). Never throws.

// Sanity bounds: anything outside this is almost certainly a copy-paste mishap,
// not a real Grafana timestamp.
const MIN_EPOCH_MS = Date.UTC(2000, 0, 1);
const MAX_FUTURE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export interface GrafanaRange {
  fromMs: number;
  toMs: number;
}

export function parseGrafanaRange(input: string, now: number = Date.now()): GrafanaRange | null {
  const raw = input.trim();
  if (!raw) return null;

  // Pull out the query portion if a full URL was pasted. URLSearchParams will
  // happily parse "from=...&to=..." too, so this just normalises the URL case.
  const queryStart = raw.indexOf('?');
  const query = queryStart >= 0 ? raw.slice(queryStart + 1) : raw;

  const params = new URLSearchParams(query);
  const fromRaw = params.get('from');
  const toRaw = params.get('to');
  if (fromRaw === null || toRaw === null) return null;

  const fromMs = Number(fromRaw);
  const toMs = Number(toRaw);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null;
  if (!Number.isInteger(fromMs) || !Number.isInteger(toMs)) return null;

  const upper = now + MAX_FUTURE_MS;
  if (fromMs < MIN_EPOCH_MS || toMs < MIN_EPOCH_MS) return null;
  if (fromMs > upper || toMs > upper) return null;
  if (fromMs >= toMs) return null;

  return { fromMs, toMs };
}
